import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { KioskNavigationContext } from '@/contexts/NavigationContext';
import type { NavigationContextType, NavigationState, SectionElements } from '@/types/navigation';
import { useAccessibilityTTS } from '@/hooks/useAccessibilityTTS';
import { useConfigStore, useModalStackStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { inputMethodTracker } from '@/lib/input-method-tracker';
import { PATHS } from '@/router/paths';

// Selector constants for better maintainability
const SELECTORS = {
    GNB_MENU_ITEM: '[class*="gnb-menu__menu-item"]',
    FOCUSABLE_ELEMENTS: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    // Add data-nav selectors for main regions
    GNB_MAIN: '[data-nav="gnb"]',
    CONTENT_MAIN: '[data-nav="content"]',
    FOOTER_MAIN: '[data-nav="footer"]',
    ZOOM_MAIN: '[data-nav="zoom"]',
} as const;

// Timing constants
const FOCUS_DELAY = 100;

interface NavigationProviderProps {
    children: React.ReactNode;
}

const routeMatchesPath = (route: string | null | undefined, pathname: string): boolean =>
    Boolean(route && (pathname === route || pathname.startsWith(`${route}/`)));
const getActiveGNBItem = (gnb: SectionElements['gnb'], pathname: string) => {
    const matches = gnb.filter((item) => routeMatchesPath(item.route, pathname));
    if (matches.length === 0) return undefined;
    return [...matches].sort((a, b) => (b.route?.length ?? 0) - (a.route?.length ?? 0))[0];
};

// Helper function to get focusable elements within kiosk layout
const getFocusableElementsInKioskLayout = (): HTMLElement[] => {
    const kioskLayout = document.querySelector('[class*="kiosk-layout"]');
    if (!kioskLayout) return [];

    const elements = Array.from(kioskLayout.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)).filter((el) => {
        const htmlEl = el as HTMLElement;
        // Filter out elements with tabindex="-1", hidden elements, and elements not in DOM
        return htmlEl.tabIndex !== -1 && !htmlEl.hidden && htmlEl.offsetParent !== null;
    }) as HTMLElement[];

    // Sort elements by tabIndex to ensure proper navigation order
    // Elements with tabIndex 0 or negative values should come after positive tabIndex elements
    return elements.sort((a, b) => {
        const aTabIndex = a.tabIndex || 0;
        const bTabIndex = b.tabIndex || 0;

        // Handle negative tabIndex (should be excluded from navigation)
        if (aTabIndex < 0 && bTabIndex >= 0) return 1;
        if (bTabIndex < 0 && aTabIndex >= 0) return -1;
        if (aTabIndex < 0 && bTabIndex < 0) return 0;

        // Handle positive tabIndex vs tabIndex 0
        if (aTabIndex > 0 && bTabIndex === 0) return -1;
        if (bTabIndex > 0 && aTabIndex === 0) return 1;

        // Both have positive tabIndex or both are 0 - sort by tabIndex value
        if (aTabIndex !== bTabIndex) {
            return aTabIndex - bTabIndex;
        }

        // If tabIndex is the same, maintain DOM order
        return (
            Array.from(kioskLayout.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)).indexOf(a) -
            Array.from(kioskLayout.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)).indexOf(b)
        );
    });
};

// Helper function to get focusable elements in a specific section
const getFocusableElementsInSection = (sectionSelector: string): HTMLElement[] => {
    const section = document.querySelector(sectionSelector);
    if (!section) return [];

    const elements = Array.from(section.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)).filter((el) => {
        const htmlEl = el as HTMLElement;
        // Filter out elements with tabindex="-1", hidden elements, and elements not in DOM
        return htmlEl.tabIndex !== -1 && !htmlEl.hidden && htmlEl.offsetParent !== null;
    }) as HTMLElement[];

    // Sort elements by tabIndex within the section
    return elements.sort((a, b) => {
        const aTabIndex = a.tabIndex || 0;
        const bTabIndex = b.tabIndex || 0;

        // Handle negative tabIndex (should be excluded from navigation)
        if (aTabIndex < 0 && bTabIndex >= 0) return 1;
        if (bTabIndex < 0 && aTabIndex >= 0) return -1;
        if (aTabIndex < 0 && bTabIndex < 0) return 0;

        // Handle positive tabIndex vs tabIndex 0
        if (aTabIndex > 0 && bTabIndex === 0) return -1;
        if (bTabIndex > 0 && aTabIndex === 0) return 1;

        // Both have positive tabIndex or both are 0 - sort by tabIndex value
        if (aTabIndex !== bTabIndex) {
            return aTabIndex - bTabIndex;
        }

        // If tabIndex is the same, maintain DOM order
        return (
            Array.from(section.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)).indexOf(a) -
            Array.from(section.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)).indexOf(b)
        );
    });
};

// Helper function to determine which section an element belongs to
const getElementSection = (element: HTMLElement): 'gnb' | 'content' | 'footer' | 'zoom' | 'unknown' => {
    const gnbContainer = getGNBContainer();
    const contentContainer = getContentContainer();
    const footerContainer = getFooterContainer();
    const zoomContainer = getZoomContainer();

    if (gnbContainer && gnbContainer.contains(element)) return 'gnb';
    if (contentContainer && contentContainer.contains(element)) return 'content';
    if (footerContainer && footerContainer.contains(element)) return 'footer';
    if (zoomContainer && zoomContainer.contains(element)) return 'zoom';
    return 'unknown';
};

// Helper function to handle arrow navigation with section-aware logic (need speak for TTS)
const createHandleArrowNavigation =
    (speakFn: (text: string) => void, voice: boolean, t: TFunction, isHomePage: boolean) =>
    (direction: 'left' | 'right', currentTarget: HTMLElement) => {
        const currentSection = getElementSection(currentTarget);

        // Define section selectors
        const sectionSelectors = {
            gnb: SELECTORS.GNB_MAIN,
            content: SELECTORS.CONTENT_MAIN,
            footer: SELECTORS.FOOTER_MAIN,
            zoom: SELECTORS.ZOOM_MAIN,
        } as const;

        // Define section descriptions for TTS
        const sectionDescriptions = {
            gnb: t('region.gnb'),
            content: t('region.content'),
            footer: t('region.footer'),
            zoom: t('region.zoom'),
        } as const;

        if (currentSection === 'unknown') {
            // Fallback to global navigation if section is unknown
            const focusableElements = getFocusableElementsInKioskLayout();
            const currentIndex = focusableElements.indexOf(currentTarget);
            let nextElement: HTMLElement;

            if (direction === 'right') {
                nextElement = focusableElements[currentIndex + 1] || focusableElements[0];
            } else {
                nextElement = focusableElements[currentIndex - 1] || focusableElements[focusableElements.length - 1];
            }

            if (nextElement) {
                focusElementWithScroll(nextElement, voice);
            }
            return;
        }

        // Get elements in current section
        const currentSectionElements = getFocusableElementsInSection(sectionSelectors[currentSection]);
        const currentIndexInSection = currentSectionElements.indexOf(currentTarget);

        if (direction === 'right') {
            // If not at the end of current section, move to next element in same section
            if (currentIndexInSection < currentSectionElements.length - 1) {
                const nextElement = currentSectionElements[currentIndexInSection + 1];
                if (nextElement) {
                    focusElementWithScroll(nextElement, voice);
                }
                return;
            }

            // If at the end of current section, move to main element of next section with section TTS
            // Default: gnb -> content -> footer -> zoom -> gnb
            // Home: gnb -> footer -> content -> zoom -> gnb
            let targetElement: HTMLElement | null = null;
            let targetSection: 'gnb' | 'content' | 'footer' | 'zoom';

            if (currentSection === 'gnb') {
                if (isHomePage) {
                    targetElement = getFooterMainElement();
                    targetSection = 'footer';
                } else {
                    targetElement = getContentMainElement();
                    targetSection = 'content';
                }
            } else if (currentSection === 'content') {
                if (isHomePage) {
                    const zoomMainElement = getZoomMainElement();
                    if (zoomMainElement) {
                        targetElement = zoomMainElement;
                        targetSection = 'zoom';
                    } else {
                        targetElement = getGNBMainElement();
                        targetSection = 'gnb';
                    }
                } else {
                    targetElement = getFooterMainElement();
                    targetSection = 'footer';
                }
            } else if (currentSection === 'footer') {
                if (isHomePage) {
                    targetElement = getContentMainElement();
                    targetSection = 'content';
                } else {
                    const zoomMainElement = getZoomMainElement();
                    if (zoomMainElement) {
                        targetElement = zoomMainElement;
                        targetSection = 'zoom';
                    } else {
                        targetElement = getGNBMainElement();
                        targetSection = 'gnb';
                    }
                }
            } else if (currentSection === 'zoom') {
                targetElement = getGNBMainElement();
                targetSection = 'gnb';
            } else {
                // Fallback to gnb if unknown section
                targetElement = getGNBMainElement();
                targetSection = 'gnb';
            }

            if (targetElement) {
                const sectionDescription = sectionDescriptions[targetSection];
                focusElementWithSectionTTS(targetElement, sectionDescription, voice, speakFn);
                return;
            }
        } else {
            // If is focusing the main region, move to last element in same section
            if (currentIndexInSection === -1) {
                const lastElement = currentSectionElements[currentSectionElements.length - 1];
                if (lastElement) {
                    focusElementWithScroll(lastElement, voice);
                }
                return;
            }

            // If not at the beginning of current section, move to previous element in same section
            if (currentIndexInSection > 0) {
                const prevElement = currentSectionElements[currentIndexInSection - 1];
                if (prevElement) {
                    focusElementWithScroll(prevElement, voice);
                }
                return;
            }

            // If at the beginning of current section, move to main element of previous section with section TTS
            // Default reverse: gnb <- content <- footer <- zoom
            // Home reverse: gnb <- footer <- content <- zoom
            let targetElement: HTMLElement | null = null;
            let targetSection: 'gnb' | 'content' | 'footer' | 'zoom';

            if (currentSection === 'content') {
                if (isHomePage) {
                    targetElement = getFooterMainElement();
                    targetSection = 'footer';
                } else {
                    targetElement = getGNBMainElement();
                    targetSection = 'gnb';
                }
            } else if (currentSection === 'footer') {
                if (isHomePage) {
                    targetElement = getGNBMainElement();
                    targetSection = 'gnb';
                } else {
                    targetElement = getContentMainElement();
                    targetSection = 'content';
                }
            } else if (currentSection === 'zoom') {
                if (isHomePage) {
                    targetElement = getContentMainElement();
                    targetSection = 'content';
                } else {
                    targetElement = getFooterMainElement();
                    targetSection = 'footer';
                }
            } else if (currentSection === 'gnb') {
                // Try zoom first, if not available, go to footer
                const zoomMainElement = getZoomMainElement();
                if (zoomMainElement) {
                    targetElement = zoomMainElement;
                    targetSection = 'zoom';
                } else {
                    targetElement = getFooterMainElement();
                    targetSection = 'footer';
                }
            } else {
                // Fallback to footer if unknown section
                targetElement = getFooterMainElement();
                targetSection = 'footer';
            }

            if (targetElement) {
                const sectionDescription = sectionDescriptions[targetSection];
                focusElementWithSectionTTS(targetElement, sectionDescription, voice, speakFn);
                return;
            }
        }

        // Fallback: if we can't find next/prev section, wrap within current section
        if (direction === 'right') {
            if (currentSectionElements.length > 0) {
                focusElementWithScroll(currentSectionElements[0], voice);
            }
        } else {
            if (currentSectionElements.length > 0) {
                focusElementWithScroll(currentSectionElements[currentSectionElements.length - 1], voice);
            }
        }
    };

// Helper functions for DOM queries
const getGNBContainer = () => document.querySelector(SELECTORS.GNB_MAIN);
const getContentContainer = () => document.querySelector(SELECTORS.CONTENT_MAIN) as HTMLElement;
const getFooterContainer = () => document.querySelector(SELECTORS.FOOTER_MAIN);
const getZoomContainer = () => document.querySelector(SELECTORS.ZOOM_MAIN);

// Helper functions to get main region elements with data-nav
const getGNBMainElement = () => {
    const element = document.querySelector(SELECTORS.GNB_MAIN) as HTMLElement;
    if (element && !element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
    }
    return element;
};

const getContentMainElement = () => {
    const element = document.querySelector(SELECTORS.CONTENT_MAIN) as HTMLElement;
    if (element && !element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
    }
    return element;
};

const getFooterMainElement = () => {
    const element = document.querySelector(SELECTORS.FOOTER_MAIN) as HTMLElement;
    if (element && !element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
    }
    return element;
};

const getZoomMainElement = () => {
    const element = document.querySelector(SELECTORS.ZOOM_MAIN) as HTMLElement;
    if (element && !element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
    }
    return element;
};

/**
 * Focuses an element with smooth scroll behavior and manages focus visibility.
 *
 * @param element - The HTML element to focus
 * @param voice - Whether voice/TTS mode is enabled
 *
 * @remarks
 * When voice mode is disabled, this function manages the `data-focus-visible` attribute:
 * - Sets to 'true' if the last user input was a navigation key (Tab, Arrow keys, Enter)
 * - Sets to 'none' if the last user input was mouse or other non-navigation keys
 * When voice mode is enabled, the focus visibility is managed by the global useFocusVisible hook.
 */
const focusElementWithScroll = (element: HTMLElement, voice: boolean) => {
    if (!voice) {
        const isNavigationKey = inputMethodTracker.isNavigationKey();
        const focusVisibleValue = isNavigationKey ? 'true' : 'none';
        document.body.setAttribute('data-focus-visible', focusVisibleValue);
    }
    element.focus();
    element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
    });
};

// Helper function to focus element and trigger section description TTS
const focusElementWithSectionTTS = (element: HTMLElement, sectionDescription: string, shouldSpeak: boolean, speakFn: (text: string) => void) => {
    focusElementWithScroll(element, shouldSpeak);

    // Trigger section description TTS instead of element content
    const timeoutId = setTimeout(() => {
        if (shouldSpeak) {
            speakFn(sectionDescription);
        }
    }, 100);

    // Return cleanup function to clear timeout
    return () => clearTimeout(timeoutId);
};

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
    const location = useLocation();
    const { speak } = useAccessibilityTTS();
    const { t } = useTranslation();
    const voice = useConfigStore((state) => state?.config?.tts?.voice);

    const isHomePage = location.pathname === PATHS.HOME.INDEX;

    // Create handleArrowNavigation with TTS support
    const handleArrowNavigation = useMemo(
        () => createHandleArrowNavigation(speak, voice, t, isHomePage),
        [speak, voice, t, isHomePage]
    );

    // State declarations grouped by purpose
    const [menuLayout, setMenuLayout] = useState<SectionElements>({ gnb: [] });
    const [shouldFocusContent, setShouldFocusContent] = useState(false);

    const [state, setState] = useState<NavigationState>({
        currentSection: 'gnb',
        focusedIndex: 0,
        activeElements: { gnb: undefined },
    });

    // Function to trigger focus to content - can be called from anywhere
    const triggerFocusContent = useCallback(() => {
        setShouldFocusContent(true);
    }, []);

    // ===== SYNC ACTIVE GNB ITEM WITH CURRENT ROUTE =====
    // useEffect(() => {
    //     const activeItem = getActiveGNBItem(menuLayout.gnb, location.pathname);
    //     setState((prev) => ({
    //         ...prev,
    //         activeElements: {
    //             ...prev.activeElements,
    //             gnb: activeItem?.id ?? undefined,
    //         },
    //     }));
    // }, [location.pathname, menuLayout.gnb]);

    useEffect(() => {
        const activeItem = getActiveGNBItem(menuLayout.gnb, location.pathname);
        const activeIndex = activeItem ? menuLayout.gnb.findIndex((item) => item.id === activeItem.id) : 0;

        setState((prev) => ({
            ...prev,
            currentSection: 'gnb',
            focusedIndex: Math.max(0, activeIndex),
            activeElements: {
                ...prev.activeElements,
                gnb: activeItem?.id ?? undefined,
            },
        }));

        const a = () => {
            const gnbContainer = getGNBContainer();
            if (gnbContainer) {
                const focusableElements = gnbContainer.querySelectorAll('button, [tabindex]');
                const targetElement = focusableElements[activeIndex] as HTMLElement;
                if (targetElement) {
                    focusElementWithScroll(targetElement, voice);
                }
            }
        };

        const timeoutId = setTimeout(a, FOCUS_DELAY);

        return () => clearTimeout(timeoutId);
    }, [menuLayout.gnb, location.pathname]);

    // ===== HANDLE FOCUS TRACKING =====
    useEffect(() => {
        const handleFocusIn = (event: FocusEvent) => {
            const target = event.target as HTMLElement;
            const gnbContainer = getGNBContainer();
            const contentContainer = getContentContainer();
            const footerContainer = getFooterContainer();
            const zoomContainer = getZoomContainer();

            if (gnbContainer && gnbContainer.contains(target)) {
                // Check if target is the container itself
                if (target === gnbContainer) {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'gnb',
                        focusedIndex: -1, // Special index for container focus
                    }));
                } else {
                    const focusableElements = gnbContainer.querySelectorAll('button, [tabindex]');
                    const focusedIndex = Array.from(focusableElements).indexOf(target);

                    setState((prev) => ({
                        ...prev,
                        currentSection: 'gnb',
                        focusedIndex: focusedIndex >= 0 && focusedIndex < menuLayout.gnb.length ? focusedIndex : 0,
                    }));
                }
            } else if (contentContainer && contentContainer.contains(target)) {
                // Check if target is the container itself
                if (target === contentContainer) {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'content',
                        focusedIndex: -1, // Special index for container focus
                    }));
                } else {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'content',
                        focusedIndex: 0,
                    }));
                }
            } else if (footerContainer && footerContainer.contains(target)) {
                // Check if target is the container itself
                if (target === footerContainer) {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'footer',
                        focusedIndex: -1, // Special index for container focus
                    }));
                } else {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'footer',
                        focusedIndex: 0,
                    }));
                }
            } else if (zoomContainer && zoomContainer.contains(target)) {
                // Check if target is the container itself
                if (target === zoomContainer) {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'zoom',
                        focusedIndex: -1, // Special index for container focus
                    }));
                } else {
                    setState((prev) => ({
                        ...prev,
                        currentSection: 'zoom',
                        focusedIndex: 0,
                    }));
                }
            }
        };

        document.addEventListener('focusin', handleFocusIn);
        return () => document.removeEventListener('focusin', handleFocusIn);
    }, [menuLayout.gnb]);

    // ===== HANDLE ENTER KEY TO FOCUS CONTENT =====
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;

            const gnbContainer = getGNBContainer();
            if (!gnbContainer) return;

            const focusableElements = gnbContainer.querySelectorAll('button, [tabindex]');
            const activeElement = document.activeElement;
            const isGNBItem = Array.from(focusableElements).includes(activeElement as HTMLElement);

            if (isGNBItem) {
                setShouldFocusContent(true);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Focus content when triggered
    useEffect(() => {
        if (!shouldFocusContent) return;

        const timeoutId = setTimeout(() => {
            const content = getContentContainer();
            if (content) {
                const allFocusable = Array.from(
                    content.querySelectorAll(SELECTORS.FOCUSABLE_ELEMENTS)
                ).filter((el) => {
                    const htmlEl = el as HTMLElement;
                    return htmlEl.tabIndex !== -1 && !htmlEl.hidden && htmlEl.offsetParent !== null;
                }) as HTMLElement[];

                if (allFocusable.length > 0) {
                    const sorted = allFocusable.sort((a, b) => {
                        const aIdx = a.tabIndex || 0;
                        const bIdx = b.tabIndex || 0;
                        if (aIdx === 0 && bIdx === 0) return 0;
                        if (aIdx === 0) return 1;
                        if (bIdx === 0) return -1;
                        return aIdx - bIdx;
                    });
                    focusElementWithScroll(sorted[0], voice);
                } else {
                    focusElementWithScroll(content, voice);
                }
            }
            setShouldFocusContent(false);
        }, FOCUS_DELAY);

        return () => clearTimeout(timeoutId);
    }, [location.pathname, shouldFocusContent, state.focusedIndex, voice]);

    // ===== HANDLE ARROW RIGHT/ ARROW LEFT KEYS FOR NAVIGATION SAME TAB/SHIFT+TAB =====
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) {
                return;
            }

            // Check if the focused element is part of a dialog or modal (focus trap area)
            const target = event.target as HTMLElement;
            const isInDialog = target.closest('[role="dialog"], [role="alertdialog"], .modal, .dialog, [data-focus-trap]');
            if (isInDialog) {
                return;
            }

            if (useModalStackStore.getState().blocksGlobalKeypad()) {
                return;
            }

            if (event.key === 'ArrowRight') {
                event.preventDefault();
                handleArrowNavigation('right', event.target as HTMLElement);
            }

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                handleArrowNavigation('left', event.target as HTMLElement);
            }
        };

        // Use lower priority by not using capture phase
        document.addEventListener('keydown', handleKeyDown, false);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, false);
        };
    }, [handleArrowNavigation, t]);

    // ===== CONTEXT VALUE WITH MEMOIZATION =====
    const contextValue: NavigationContextType = useMemo(
        () => ({
            menuLayout,
            setMenuLayout,
            state,
            setState,
            triggerFocusContent,
        }),
        [menuLayout, state, triggerFocusContent]
    );

    return <KioskNavigationContext.Provider value={contextValue}>{children}</KioskNavigationContext.Provider>;
};
